import cadviewer from "cadviewer";
import { createContext, useCallback, useEffect, useMemo, useState } from "react";
import { NodeId } from "../components/TreeView/types";
import useConfig from "../demos/hooks/useConfig";
import EventSystem from "../events/EventSystem";
import {
  Employee,
  Plan,
  PlanDetail,
  PlanInfo,
  SpaceObjectProps,
  User,
  View,
} from "../types";
import { ViewsPlanType } from "../types/view";
import cleanKeys from "../utils/object";
import { useLocalStorage } from "../utils/useLocalStorage";

// Import package.json to get version
import packageJson from "../../package.json";

export const extractFileNameFromPath = (filePath: string) => {
  // Check if the path separator is '/' (Unix-like systems)
  if (filePath.includes("/")) {
    return filePath.split("/").pop(); // Get the last element after splitting by '/'
  } else if (filePath.includes("\\")) {
    // Check if the path separator is '\' (Windows)
    return filePath.split("\\").pop(); // Get the last element after splitting by '\'
  } else {
    // If the path doesn't contain any path separator, return the whole string
    return filePath;
  }
};

export interface Layer {
  layer: string;
  color: string;
}

export interface ColorEntry {
  plan: string;
  hex: string;
  filltype: string;
  filltype2: string | number;
  text: string;
}

export interface PlanColorData {
  [planName: string]: ColorEntry[];
}

export interface SpaceDistance {
  spaceNumber: string;
  Distance: number;
}

export interface SpaceWithDistances {
  spaceNumber: string;
  distances: SpaceDistance[];
}

export interface DistanceData {
  planType: string;
  propertyNumber: string;
  planName: string;
  spaces: SpaceWithDistances[];
}

// Room data from server-side Excel
export interface RoomData {
  dwgname: string;
  bl_id: string;
  fl_id: string;
  rm_id: string;
  rm_cat: string;
  rm_type: string;
  area: number;
  dp_id: string;
  dp_name: string;
  dv_id: string;
  org_campus_id: string;
  org_admin_id: string;
  bu_id: string;
  org_school_id: string;
  org_dv_id: string;
  [key: string]: string | number; // Allow additional dynamic fields
}

export interface CanvasInstance {
  id: string;
  filePath?: string;
  fileName?: string;
}

export interface CadviewerContextProps {
  currentDrawingPath?: string;
  setCurrentDrawingPath: (path: string | undefined) => void;
  spaceObjects?: SpaceObjectProps[];
  employees: Employee[];
  plansInfo: PlanInfo | undefined;
  setSpaceObjects: () => void;
  setEmployees: (employees: Employee[]) => void;
  setSelectedSpaceObjectID: (id: string | undefined) => void;
  setSelectedPlan: (plan: Plan) => void;
  setSelectedPlanDetail: (planDetail: PlanDetail) => void;
  selectedSpaceObjectID?: string;
  selectedPlan?: Plan;
  selectedPlanDetail?: PlanDetail;
  user: User | null;
  setUser: (user: User | null) => void;
  disableFeature: boolean;
  // tab information in bottom panel
  tabIndex: string;
  setTabIndex: (tabIndex: string) => void;
  fetchViewJson: () => Promise<void>;
  views: ViewsPlanType[];
  planTypes: string[];
  currentPlanType?: string;
  setCurrentPlanType: (planType: string) => void;
  canvasInstances: CanvasInstance[];
  layerColorsOnLoad: Layer[];
  setLayerColorsOnLoad: (layers: Layer[]) => void;
  newCanvasInstance: (callback?: () => void) => void;
  removeCanvasInstance: (id: string) => void;
  currentCanvasInstance?: string;
  setCurrentCanvasInstance: (id: string) => void;
  handleSelector: boolean;
  setHandleSelector: (handleSelector: boolean) => void;
  expendedIds: NodeId[];
  setExpendedIds: (expendedIds: NodeId[]) => void;
  multiSelectArray: string[];
  setMultiSelectArray: (multiSelectArray: string[]) => void;
  planColorData: PlanColorData | null;
  fetchLayerColorsCSV: (path: string) => Promise<void>;
  distanceData: DistanceData | null;
  fetchDistanceJson: (path: string) => Promise<void>;
  // Server-side Excel Room Data
  roomsData: RoomData[] | null;
  fetchRoomsData: () => Promise<void>;
  // App version from package.json
  appVersion: string;
  // Enable drag selection mode for spaces
  enableDragSelectSpaces: () => void;
  // Space selection for AI mode
  isSelectingSpaceForAI: boolean;
  setIsSelectingSpaceForAI: (value: boolean) => void;
  spaceSelectedForAI: { spaceNumber: string; planName: string; propertyName: string } | null;
  setSpaceSelectedForAI: (data: { spaceNumber: string; planName: string; propertyName: string } | null) => void;
  // Drawing loaded state (set by CADViewer cvjs_OnLoadEnd)
  drawingLoaded: boolean;
  setDrawingLoaded: (loaded: boolean) => void;
  // Monotonic counter incremented on each drawing load start (immune to React batching)
  drawingLoadGeneration: number;
  signalDrawingLoadStart: () => void;
  // Counter incremented on every cvjs_OnLoadEnd event (supports multi-pass CADViewer loads)
  drawingLoadEndCount: number;
  // Query params for deep linking
  querySelectedView: string | null;
  queryStoreId: string | null;
}

export const CadviewerContext = createContext<CadviewerContextProps>({
  spaceObjects: undefined,
  setCurrentDrawingPath: () => { },
  currentDrawingPath: undefined,
  employees: [],
  plansInfo: undefined,
  setEmployees: () => { },
  setSpaceObjects: () => { },
  setSelectedSpaceObjectID: () => { },
  setSelectedPlan: () => { },
  setSelectedPlanDetail: () => { },
  selectedSpaceObjectID: undefined,
  selectedPlan: undefined,
  selectedPlanDetail: undefined,
  user: null,
  setUser: () => { },
  disableFeature: false,
  tabIndex: "tenant_information",
  setTabIndex: () => { },
  fetchViewJson: () => Promise.resolve(),
  views: [],
  planTypes: [],
  currentPlanType: undefined,
  setCurrentPlanType: () => { },
  canvasInstances: [],
  layerColorsOnLoad: [],
  setLayerColorsOnLoad: () => { },
  newCanvasInstance: () => { },
  currentCanvasInstance: undefined,
  setCurrentCanvasInstance: () => { },
  removeCanvasInstance: () => { },
  handleSelector: false,
  setHandleSelector: () => { },
  expendedIds: [],
  setExpendedIds: () => { },
  multiSelectArray: [],
  setMultiSelectArray: () => { },
  planColorData: null,
  fetchLayerColorsCSV: () => Promise.resolve(),
  distanceData: null,
  fetchDistanceJson: () => Promise.resolve(),
  roomsData: null,
  fetchRoomsData: () => Promise.resolve(),
  appVersion: packageJson.version,
  enableDragSelectSpaces: () => { },
  isSelectingSpaceForAI: false,
  setIsSelectingSpaceForAI: () => { },
  spaceSelectedForAI: null,
  setSpaceSelectedForAI: () => { },
  drawingLoaded: false,
  setDrawingLoaded: () => { },
  drawingLoadGeneration: 0,
  signalDrawingLoadStart: () => { },
  drawingLoadEndCount: 0,
  querySelectedView: null,
  queryStoreId: null,
});

export const CadviewerContextProvider = ({ children }: any) => {
  const { config } = useConfig();

  // Query params for deep linking - using window.location.search 
  // because this provider is rendered before the Router
  const querySelectedView = useMemo(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get("selectedView");
  }, []);
  const queryStoreId = useMemo(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get("storeId");
  }, []);

  let {
    propertyJSONFilesLocation,
    globalApplicationFilesLocation,
    postFixServerToken,
    ServerBackEndUrl,
    globalApplicationViews,
    propertyJSONFilesEnableDistance,
    propertyJSONFilesEnableLayerColors,
  } = config;

  if (config.debugMode) console.log("CadviewerContextProvider config propertyJSONFilesEnableDistance:", propertyJSONFilesEnableDistance);

  const [spaceObjects, setSpaceObjects] = useState<SpaceObjectProps[]>([]);

  const [selectedSpaceObjectID, setSelectedSpaceObjectID] = useState<
    string | undefined
  >(undefined);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [selectedPlan, setSelectedPlan] = useState<Plan | undefined>(undefined);
  const [selectedPlanDetail, setSelectedPlanDetail] = useState<
    PlanDetail | undefined
  >(undefined);
  const { setItem, removeItem } = useLocalStorage();
  const [user, setUser] = useState<User | null>(null);
  const [plansInfo, setPlansInfo] = useState<PlanInfo | undefined>();
  const [tabIndex, setTabIndex] = useState<string>("tenant_information");
  const [views, setViews] = useState<ViewsPlanType[]>([]);
  const [currentPlanType, setCurrentPlanType] = useState<string | undefined>(
    undefined
  );
  const [planTypes, setPlanTypes] = useState<string[]>([]);
  const [currentDrawingPath, setCurrentDrawingPath] = useState<
    string | undefined
  >(undefined);
  const [expendedIds, setExpendedIds] = useState<NodeId[]>([]);
  const [planColorData, setPlanColorData] = useState<PlanColorData | null>(
    null
  );
  const [distanceData, setDistanceData] = useState<DistanceData | null>(null);
  const [roomsData, setRoomsData] = useState<RoomData[] | null>(null);

  const [handleSelector, setHandleSelector] = useState<boolean>(false);
  const [multiSelectArray, setMultiSelectArray] = useState<string[]>([]);
  const [drawingLoaded, setDrawingLoaded] = useState<boolean>(false);
  const [drawingLoadGeneration, setDrawingLoadGeneration] = useState(0);

  // Helper: call this instead of setDrawingLoaded(false) to ensure useDeepLinkView
  // always detects a new load cycle, even when React 18 batching merges false+true.
  const signalDrawingLoadStart = useCallback(() => {
    setDrawingLoadGeneration((prev) => {
      const next = prev + 1;
      console.log(`[DeepLink] signalDrawingLoadStart() — drawingLoaded→false, generation: ${prev}→${next}`);
      return next;
    });
    setDrawingLoaded(false);
  }, []);

  const [drawingLoadEndCount, setDrawingLoadEndCount] = useState(0);

  // Listen for cvjs_onLoadEnd event to set drawingLoaded (avoids stale closure in CADViewer callback)
  // Also increments drawingLoadEndCount so useDeepLinkView can re-apply the view after each
  // onLoadEnd (CADViewer fires multiple onLoadEnd events per load: splash, conversion, final SVG).
  useEffect(() => {
    const handler = () => {
      console.log("[DeepLink] Context: EventSystem cvjs_onLoadEnd received — setDrawingLoaded(true), incrementing drawingLoadEndCount");
      setDrawingLoaded(true);
      setDrawingLoadEndCount((prev) => prev + 1);
    };
    EventSystem.subscribe("cadviewer.cvjs_onLoadEnd", handler);
    return () => EventSystem.unsubscribe("cadviewer.cvjs_onLoadEnd", handler);
  }, []);

  const [isSelectingSpaceForAI, setIsSelectingSpaceForAI] = useState<boolean>(false);
  const [spaceSelectedForAI, setSpaceSelectedForAI] = useState<{ spaceNumber: string; planName: string; propertyName: string } | null>(null);

  const [canvasInstances, setCanvasInstances] = useState<CanvasInstance[]>([
    {
      id: "floorPlan",
      fileName: config.initFileName
        ? extractFileNameFromPath(config.initFileName)
        : undefined,
      filePath: config.initFileName || undefined,
    },
  ]);
  const [currentCanvasInstance, setCurrentCanvasInstance] = useState<
    string | undefined
  >(undefined);

  const [layerColorsOnLoad, setLayerColorsOnLoad] = useState<Layer[]>(
    config.defaultLayerColorsOnLoad
  );

  // Function to convert RGB to Hex
  const rgbToHex = (r: number, g: number, b: number): string => {
    return (
      "#" +
      ((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1).toUpperCase()
    );
  };

  // Function to fetch and parse the CSV file
  const fetchLayerColorsCSV = useCallback(
    async (path: string) => {
      if (!propertyJSONFilesEnableLayerColors) {
        return;
      }

      try {
        // Get the directory of the JSON file
        const lastSlashIndex = path.lastIndexOf("/");
        const directory =
          lastSlashIndex !== -1 ? path.substring(0, lastSlashIndex + 1) : "";

        // Build the path to the LayerColors.csv file
        const csvFilePath = directory + "LayerColors.csv";

        // Build the full URL to the CSV file
        const url = (propertyJSONFilesLocation + "" + csvFilePath).replace(
          "/v2/json/property/retail/",
          "/json/property/"
        );

        if (config.debugMode) console.log("Loading layer colors from:", url);

        let headers = undefined;
        if (postFixServerToken) {
          headers = {
            "Content-Type": "application/json",
          };
        }

        if (config.bearerToken) {
          headers = {
            ...(headers ?? {}),
            Authorization: `${config.bearerToken}`,
          };
        }

        var endpoint = "loadtextfile/proxy";
        if (ServerBackEndUrl.substring(ServerBackEndUrl.length - 1) === "/") {
          // do nothing
        } else {
          endpoint = "/" + endpoint;
        }

        const response = await fetch(
          postFixServerToken ? ServerBackEndUrl + endpoint : url,
          {
            method: postFixServerToken ? "POST" : "GET",
            headers,
            body: postFixServerToken ? JSON.stringify({ url: url }) : undefined,
            redirect: "follow",
          }
        );

        if (!response.ok) {
          throw new Error("Network response was not ok");
        }

        const csvText = await response.text();
        const lines = csvText.split("\n").filter((line) => line.trim() !== "");

        const colorData: PlanColorData = {};

        for (const line of lines) {
          // Skip empty lines
          if (line.trim() === "" || line.trim().startsWith(",,,,,,")) {
            continue;
          }

          const parts = line.split(",");
          if (parts.length >= 6) {
            const planName = parts[0].trim();
            const r = parseInt(parts[1].trim(), 10);
            const g = parseInt(parts[2].trim(), 10);
            const b = parseInt(parts[3].trim(), 10);
            const filltype = parts[4].trim();
            const filltype2 = parts[5].trim();
            // Join the rest for the text part, which might contain commas
            const text = parts.slice(6).join(",").trim();

            // Create hex color from RGB
            const hexColor = rgbToHex(r, g, b);

            if (!colorData[planName]) {
              colorData[planName] = [];
            }

            colorData[planName].push({
              plan: planName,
              hex: hexColor,
              filltype,
              filltype2,
              text,
            });
          }
        }

        setPlanColorData(colorData);
        if (config.debugMode) console.log("Parsed LayerColors.csv:", colorData);
      } catch (error: any) {
        if (config.debugMode) console.error("Error fetching or parsing LayerColors.csv:", error);
        setPlanColorData(null);
      }
    },
    [
      propertyJSONFilesLocation,
      postFixServerToken,
      ServerBackEndUrl,
      propertyJSONFilesEnableLayerColors,
    ]
  );

  // Function to fetch distance JSON file
  const fetchDistanceJson = useCallback(
    async (path: string) => {
      if (!propertyJSONFilesEnableDistance) {
        return;
      }

      try {
        // Replace .json with -distances.json to get the distance file path
        const distanceFilePath = path.replace(".json", "-distances.json");

        // todo remove the replace when the json files are in the right location
        const url = (propertyJSONFilesLocation + "" + distanceFilePath).replace(
          "/v2/json/property/retail/",
          "/json/property/"
        );

        var endpoint = "loadjsonpostfix/proxy";
        if (ServerBackEndUrl.substring(ServerBackEndUrl.length - 1) === "/") {
          // do nothing
        } else {
          endpoint = "/" + endpoint;
        }

        if (config.debugMode) console.log("Loading distance data from:", url);

        let headers = undefined;
        if (postFixServerToken) {
          headers = {
            "Content-Type": "application/json",
          };
        }

        if (config.bearerToken) {
          headers = {
            ...(headers ?? {}),
            Authorization: `${config.bearerToken}`,
          };
        }

        const response = await fetch(
          postFixServerToken ? ServerBackEndUrl + endpoint : url,
          {
            method: postFixServerToken ? "POST" : "GET",
            headers,
            body: postFixServerToken ? JSON.stringify({ url: url }) : undefined,
            redirect: "follow",
          }
        );

        if (!response.ok) {
          throw new Error("Network response was not ok");
        }

        let data = await response.json();
        if (config.debugMode) console.log("Distance data loaded:", data);

        setDistanceData(cleanKeys(data.data));
      } catch (error: any) {
        if (config.debugMode) console.error("Error fetching distance data:", error);
        setDistanceData(null);
      }
    },
    [
      propertyJSONFilesLocation,
      postFixServerToken,
      ServerBackEndUrl,
      propertyJSONFilesEnableDistance,
    ]
  );

  // Function to fetch rooms data from server-side Excel endpoint
  const fetchRoomsData = useCallback(async () => {
    if (!config.fetchRoomsDataFromServer || !config.roomsDataEndpoint) {
      return;
    }

    try {
      const url = ServerBackEndUrl + config.roomsDataEndpoint;
      if (config.debugMode) console.log("Fetching rooms data from:", url);

      let headers: HeadersInit = {};
      if (config.bearerToken) {
        headers = {
          Authorization: `${config.bearerToken}`,
        };
      }

      const response = await fetch(url, {
        method: "GET",
        headers,
        redirect: "follow",
      });

      if (!response.ok) {
        throw new Error("Network response was not ok");
      }

      const result = await response.json();
      if (result.success && result.data) {
        if (config.debugMode) console.log("Rooms data loaded:", result.totalRows, "rows");
        setRoomsData(result.data);
      } else {
        if (config.debugMode) console.error("Rooms data fetch failed:", result.error);
        setRoomsData(null);
      }
    } catch (error: any) {
      if (config.debugMode) console.error("Error fetching rooms data:", error);
      setRoomsData(null);
    }
  }, [ServerBackEndUrl, config.fetchRoomsDataFromServer, config.roomsDataEndpoint, config.bearerToken, config.debugMode]);

  const addUser = (user: User | null) => {
    if (user === null) {
      setUser(null);
      removeItem("auth_user");
    } else {
      setUser(user);
      setItem("auth_user", JSON.stringify(user));
    }
  };

  const fetchPlanJson = useCallback(
    async (path: string) => {
      setPlansInfo(undefined);
      try {
        // todo remove the replace when the json files are in the right location
        const url = (propertyJSONFilesLocation + "" + path).replace(
          "/v2/json/property/retail/",
          "/json/property/"
        );

        var endpoint = "loadjsonpostfix/proxy";
        if (ServerBackEndUrl.substring(ServerBackEndUrl.length - 1) === "/") {
          // do nothing
        } else {
          endpoint = "/" + endpoint;
        }

        if (config.debugMode) console.log("2 data:" + endpoint);

        let headers = undefined;
        if (postFixServerToken) {
          headers = {
            "Content-Type": "application/json",
          };
        }

        if (config.bearerToken) {
          headers = {
            ...(headers ?? {}),
            Authorization: `${config.bearerToken}`,
          };
        }

        const response = await fetch(
          postFixServerToken ? ServerBackEndUrl + endpoint : url,
          {
            method: postFixServerToken ? "POST" : "GET",
            headers,
            body: postFixServerToken ? JSON.stringify({ url: url }) : undefined,
            redirect: "follow",
          }
        );

        if (!response.ok) {
          throw new Error("Network response was not ok");
        }
        let data = await response.json();

        if (config.debugMode) console.log("2 data:" + data);
        setPlansInfo(cleanKeys(data.data));

        // If distance data is enabled, fetch the distance file as well
        if (propertyJSONFilesEnableDistance) {
          fetchDistanceJson(path);
        }

        // If layer colors are enabled, fetch the LayerColors.csv file
        if (propertyJSONFilesEnableLayerColors) {
          fetchLayerColorsCSV(path);
        }
      } catch (error: any) { }
    },
    [
      propertyJSONFilesLocation,
      postFixServerToken,
      ServerBackEndUrl,
      propertyJSONFilesEnableDistance,
      fetchDistanceJson,
      propertyJSONFilesEnableLayerColors,
      fetchLayerColorsCSV,
    ]
  );

  const fetchViewJson = useCallback(async () => {
    try {
      // test, proper url is in the globalApplicationFilesLocation+""+globalApplicationViews
      //const url = "https://zthcvtest.blob.core.windows.net/test-cadviewer/examples/json/application/views.json";
      // // globalApplicationFilesLocation+""+globalApplicationViews;

      const url = globalApplicationFilesLocation + "" + globalApplicationViews;

      var endpoint = "loadjsonpostfix/proxy";
      if (ServerBackEndUrl.substring(ServerBackEndUrl.length - 1) === "/") {
        // do nothing
      } else {
        endpoint = "/" + endpoint;
      }

      if (config.debugMode) console.log("3 data:" + endpoint);

      let headers = undefined;
      if (postFixServerToken) {
        headers = {
          "Content-Type": "application/json",
        };
      }
      if (config.bearerToken) {
        headers = {
          ...(headers ?? {}),
          Authorization: `${config.bearerToken}`,
        };
      }

      const response = await fetch(
        postFixServerToken ? ServerBackEndUrl + endpoint : url,
        {
          method: postFixServerToken ? "POST" : "GET",
          headers,
          body: postFixServerToken ? JSON.stringify({ url: url }) : null,
          redirect: "follow",
        }
      );

      if (!response.ok) {
        throw new Error("Network response was not ok");
      }
      const data = await response.json();
      // loop over data.data and add a selected property to each object
      data.data = data.data.map((viewPlan: ViewsPlanType) => {
        return {
          ...viewPlan,
          views: viewPlan.views.map((view: View) => {
            view.selected = (view.view ?? view.View) === viewPlan.selected;
            return view;
          }),
        };
      });
      const tmpViews = cleanKeys(data.data);

      setViews(tmpViews);
      setPlanTypes(
        tmpViews.map((viewPlan: ViewsPlanType) => viewPlan.planType)
      );
      if (currentPlanType === undefined) {
        setCurrentPlanType(tmpViews[0]?.planType || undefined);
      }
    } catch (error: any) { }
  }, [
    globalApplicationFilesLocation,
    globalApplicationViews,
    postFixServerToken,
    ServerBackEndUrl,
    currentPlanType,
  ]);

  useEffect(() => {
    if (selectedPlanDetail) {
      //            fetchPlanJson("3442-LP2-v2.json")//selectedPlanDetail.dataFileName);
      fetchPlanJson(selectedPlanDetail.dataFileName);
    }
  }, [fetchPlanJson, selectedPlanDetail]);

  useEffect(() => {
    fetchViewJson();
  }, [fetchViewJson]);

  // Fetch rooms data from server on load if enabled
  useEffect(() => {
    if (config.fetchRoomsDataFromServer) {
      fetchRoomsData();
    }
  }, [fetchRoomsData, config.fetchRoomsDataFromServer]);

  useEffect(() => {
    if (!currentCanvasInstance && canvasInstances)
      setCurrentCanvasInstance(canvasInstances[0].id);
  }, [canvasInstances, currentCanvasInstance]);

  // Listen for spacesSelected event from drag-select and update multiSelectArray
  useEffect(() => {
    const handleSpacesSelected = (event: any) => {
      const selectedSpaces = event.detail?.selectedSpaces || [];
      console.log('Spaces selected via drag:', selectedSpaces);
      setMultiSelectArray(selectedSpaces);
      
      // Auto-populate AI chat drawer "Spaces Selected" list
      EventSystem.publish("cadviewer.auto_add_spaces", selectedSpaces);
    };

    window.addEventListener('spacesSelected', handleSpacesSelected);
    return () => {
      window.removeEventListener('spacesSelected', handleSpacesSelected);
    };
  }, []);

  /*
    useEffect(() => {
        const index = currentCanvasInstance ? canvasInstances.findIndex(instance => instance.id === currentCanvasInstance) : 0;
        cadviewer.cvjs_setActiveFloorplanIndex(index);
    }, [canvasInstances]);
  */

  if (config.debugMode) console.log({ planColorData, distanceData });

  return (
    <CadviewerContext.Provider
      value={{
        canvasInstances,
        newCanvasInstance: (callback) => {
          const id = "floorPlan-" + new Date().getTime();
          setCanvasInstances([
            ...canvasInstances,
            {
              id,
              fileName: undefined,
              filePath: undefined,
            },
          ]);
          setCurrentCanvasInstance(id);
          if (callback) callback();
        },
        removeCanvasInstance: (id: string) => {
          const index = canvasInstances.findIndex(
            (instance) => instance.id === id
          );
          if (
            id === currentCanvasInstance &&
            index === canvasInstances.length - 1
          ) {
            setCurrentCanvasInstance(canvasInstances[index - 1]?.id);
          } else if (id === currentCanvasInstance) {
            setCurrentCanvasInstance(canvasInstances[index + 1]?.id);
          }
          setCanvasInstances((instances) =>
            instances.filter((instance) => instance.id !== id)
          );
        },
        fetchViewJson,
        currentCanvasInstance,
        expendedIds,
        setExpendedIds,
        setCurrentCanvasInstance,
        currentDrawingPath,
        setCurrentDrawingPath: (path) => {
          if (path) {
            setCanvasInstances((instances) =>
              instances.map((instance) => ({
                ...instance,
                filePath:
                  instance.id === currentCanvasInstance
                    ? path
                    : instance.filePath,
                fileName:
                  instance.id === currentCanvasInstance
                    ? extractFileNameFromPath(path)
                    : instance.fileName,
              }))
            );
          }
          setCurrentDrawingPath(path);
        },
        spaceObjects,
        employees,
        plansInfo,
        setEmployees,
        setSpaceObjects: () => {
          const allSpaceObjects = cadviewer.cvjs_returnAllSpaceObjects();
          setSpaceObjects(allSpaceObjects?.SpaceObjects ?? []);
        },
        setSelectedSpaceObjectID,
        selectedSpaceObjectID,
        selectedPlan,
        setSelectedPlan,
        selectedPlanDetail,
        setSelectedPlanDetail,
        user,
        setUser: addUser,
        disableFeature:
          config.disableAllFeaturesIfNotLoggedIn &&
          config.enableLoginFeature &&
          !user,
        tabIndex,
        setTabIndex,
        views,
        planTypes,
        currentPlanType,
        setCurrentPlanType,
        handleSelector,
        setHandleSelector,
        layerColorsOnLoad,
        setLayerColorsOnLoad,
        multiSelectArray,
        setMultiSelectArray,
        planColorData,
        fetchLayerColorsCSV,
        distanceData,
        fetchDistanceJson,
        roomsData,
        fetchRoomsData,
        appVersion: packageJson.version,
        enableDragSelectSpaces: () => {
          // Dispatch a custom event that CADViewerCustomCanvasVizQueryBrookfield will listen to
          window.dispatchEvent(new CustomEvent('enableDragSelectSpaces'));
        },
        isSelectingSpaceForAI,
        setIsSelectingSpaceForAI: (value: boolean) => {
          setIsSelectingSpaceForAI(value);
        },
        spaceSelectedForAI,
        setSpaceSelectedForAI,
        drawingLoaded,
        setDrawingLoaded,
        drawingLoadGeneration,
        signalDrawingLoadStart,
        drawingLoadEndCount,
        querySelectedView,
        queryStoreId,
      }}
    >
      {children}
    </CadviewerContext.Provider>
  );
};
